import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { ArrowLeft, Plus, Trash2, Search } from 'lucide-react';
import BarcodeScanInput from '../components/BarcodeScanInput';

const PO_AUTOSAVE_KEY = 'webb_po_draft';

const CreatePurchaseOrder = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);

  // Restore autosaved state
  const saved = (() => { try { return JSON.parse(localStorage.getItem(PO_AUTOSAVE_KEY)); } catch { return null; } })();

  const [selectedVendor, setSelectedVendor] = useState(saved?.selectedVendor || '');
  const [vendorInfo, setVendorInfo] = useState(saved?.vendorInfo || null);
  const [lineItems, setLineItems] = useState(saved?.lineItems || []);
  const [remarks, setRemarks] = useState(saved?.remarks || '');
  const [paymentTerms, setPaymentTerms] = useState(saved?.paymentTerms || '');
  const [location, setLocation] = useState(saved?.location || '');
  const [saving, setSaving] = useState(false);
  const [orderDate, setOrderDate] = useState(saved?.orderDate || new Date().toISOString().split('T')[0]);
  const [draftSaved, setDraftSaved] = useState(false);
  const [vendorAddress, setVendorAddress] = useState(saved?.vendorAddress || '');
  const [vendorCity, setVendorCity] = useState(saved?.vendorCity || '');
  const [vendorState, setVendorState] = useState(saved?.vendorState || '');
  const [vendorZip, setVendorZip] = useState(saved?.vendorZip || '');
  const [vendorCountry, setVendorCountry] = useState(saved?.vendorCountry || '');
  const [vendorContact, setVendorContact] = useState(saved?.vendorContact || '');
  const [vendorEmail, setVendorEmail] = useState(saved?.vendorEmail || '');
  const [vendorPhone, setVendorPhone] = useState(saved?.vendorPhone || '');

  // Product search
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [showProductSearch, setShowProductSearch] = useState(false);

  useEffect(() => {
    const loadVendors = async () => {
      try {
        const data = await api.getVendors();
        setVendors(data);
      } catch (err) { console.error(err); }
    };
    loadVendors();
  }, []);

  // Autosave form state to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem(PO_AUTOSAVE_KEY, JSON.stringify({
        selectedVendor, vendorInfo, lineItems, remarks, paymentTerms,
        location, orderDate, vendorAddress, vendorCity, vendorState,
        vendorZip, vendorCountry, vendorContact, vendorEmail, vendorPhone,
      }));
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 2000);
    }, 500);
    return () => clearTimeout(timer);
  }, [selectedVendor, vendorInfo, lineItems, remarks, paymentTerms,
      location, orderDate, vendorAddress, vendorCity, vendorState,
      vendorZip, vendorCountry, vendorContact, vendorEmail, vendorPhone]);

  const handleVendorSelect = (vendorId) => {
    setSelectedVendor(vendorId);
    if (!vendorId) {
      setVendorInfo(null);
      setVendorAddress(''); setVendorCity(''); setVendorState(''); setVendorZip('');
      setVendorCountry(''); setVendorContact(''); setVendorEmail(''); setVendorPhone('');
      setPaymentTerms('');
      return;
    }
    const v = vendors.find(vendor => vendor.id === vendorId);
    if (v) {
      setVendorInfo(v);
      setVendorAddress(v.address || '');
      setVendorCity(v.city || '');
      setVendorState(v.state || '');
      setVendorZip(v.zip_code || '');
      setVendorCountry(v.country || '');
      setVendorContact(v.contact_person || '');
      setVendorEmail(v.email || '');
      setVendorPhone(v.phone || '');
      setPaymentTerms(v.payment_terms || '');
    }
  };

  useEffect(() => {
    if (productSearch.length < 2) { setProductResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        // limit=500 so we never truncate the list (was capped at 20 → user reported list ending early).
        const products = await api.getProducts({ search: productSearch, limit: 500 });
        setProductResults(Array.isArray(products) ? products : []);
      } catch (err) { console.error(err); }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch]);

  const addProductItem = (product) => {
    const existing = lineItems.find(li => li.sku === product.sku);
    if (existing) {
      setLineItems(lineItems.map(li =>
        li.sku === product.sku ? { ...li, quantity_ordered: li.quantity_ordered + 1, total: (li.quantity_ordered + 1) * li.unit_cost } : li
      ));
    } else {
      setLineItems([...lineItems, {
        variant_name: product.name,
        variant_id: product.variant_id || product.variants?.[0]?.variant_id || '',
        sku: product.sku || '',
        description: product.description || '',
        quantity_ordered: 1,
        unit_cost: product.cost_price || product.unit_price || 0,
        total: product.cost_price || product.unit_price || 0,
      }]);
    }
    setProductSearch('');
    setShowProductSearch(false);
  };

  const addManualItem = () => {
    setLineItems([...lineItems, {
      variant_name: '', sku: '', description: '',
      quantity_ordered: 1, unit_cost: 0, total: 0,
    }]);
  };

  const updateItem = (index, field, value) => {
    setLineItems(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], [field]: value };
      if (field === 'quantity_ordered' || field === 'unit_cost') {
        const qty = field === 'quantity_ordered' ? parseFloat(value) || 0 : newItems[index].quantity_ordered;
        const cost = field === 'unit_cost' ? parseFloat(value) || 0 : newItems[index].unit_cost;
        newItems[index].total = qty * cost;
      }
      return newItems;
    });
  };

  const removeItem = (index) => setLineItems(prev => prev.filter((_, i) => i !== index));

  const total = lineItems.reduce((sum, li) => sum + (li.total || 0), 0);

  const handleSubmit = async (e, saveAsDraft = false) => {
    e.preventDefault();
    if (!selectedVendor) { alert('Please select a vendor'); return; }
    if (lineItems.length === 0) { alert('Add at least one item'); return; }

    setSaving(true);
    try {
      const vendor = vendors.find(v => v.id === selectedVendor);
      await api.createPurchaseOrder({
        vendor_id: selectedVendor,
        vendor_name: vendor?.name || '',
        vendor_address: vendorAddress,
        vendor_city: vendorCity,
        vendor_state: vendorState,
        vendor_zip: vendorZip,
        vendor_country: vendorCountry,
        vendor_contact: vendorContact,
        vendor_email: vendorEmail,
        vendor_phone: vendorPhone,
        line_items: lineItems,
        total,
        remarks,
        payment_terms: paymentTerms,
        location,
        order_date: orderDate || null,
        status: saveAsDraft ? 'draft' : 'ordered',
      });
      localStorage.removeItem(PO_AUTOSAVE_KEY);
      if (saveAsDraft) {
        alert('PO saved as Draft. NOTE: This PO will NOT be active until it is confirmed.');
      }
      navigate('/purchase-orders');
    } catch (err) {
      alert('Failed to create PO: ' + err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="create-po-page">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-muted rounded-md" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h2 className="text-2xl font-semibold">New Purchase Order</h2>
        {draftSaved && (
          <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full animate-pulse" data-testid="draft-saved-indicator">Draft saved</span>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Vendor & Details */}
        <div className="bg-card rounded-lg border border-border p-5">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Order Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Vendor *</label>
              <select value={selectedVendor} onChange={(e) => handleVendorSelect(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md" data-testid="po-vendor-select">
                <option value="">Select vendor...</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Order Date</label>
              <input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md" data-testid="po-order-date" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Payment Terms</label>
              <select value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md" data-testid="po-terms">
                <option value="">Select...</option>
                <option value="Net 15">Net 15</option>
                <option value="Net 30">Net 30</option>
                <option value="Net 60">Net 60</option>
                <option value="Net 90">Net 90</option>
                <option value="Due on Receipt">Due on Receipt</option>
                <option value="COD">COD</option>
                <option value="Prepaid">Prepaid</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Location</label>
              <input type="text" value={location} onChange={(e) => setLocation(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md" data-testid="po-location" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Remarks</label>
              <input type="text" value={remarks} onChange={(e) => setRemarks(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md" data-testid="po-remarks" />
            </div>
          </div>
        </div>

        {/* Vendor Info (auto-populated) */}
        {vendorInfo && (
          <div className="bg-card rounded-lg border border-border p-5" data-testid="vendor-info-section">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Vendor Information</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Contact Person</label>
                <input type="text" value={vendorContact} onChange={(e) => setVendorContact(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="po-vendor-contact" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Email</label>
                <input type="email" value={vendorEmail} onChange={(e) => setVendorEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="po-vendor-email" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Phone</label>
                <input type="text" value={vendorPhone} onChange={(e) => setVendorPhone(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="po-vendor-phone" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted-foreground mb-1">Address</label>
                <input type="text" value={vendorAddress} onChange={(e) => setVendorAddress(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="po-vendor-address" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">City</label>
                <input type="text" value={vendorCity} onChange={(e) => setVendorCity(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="po-vendor-city" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">State</label>
                <input type="text" value={vendorState} onChange={(e) => setVendorState(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="po-vendor-state" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Zip</label>
                <input type="text" value={vendorZip} onChange={(e) => setVendorZip(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="po-vendor-zip" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Country</label>
                <input type="text" value={vendorCountry} onChange={(e) => setVendorCountry(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="po-vendor-country" />
              </div>
            </div>
          </div>
        )}

        {/* Line Items */}
        <div className="bg-card rounded-lg border border-border">
          <div className="p-5 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Line Items</h3>
            <div className="flex gap-2">
              <div className="relative">
                <button type="button" onClick={() => setShowProductSearch(!showProductSearch)}
                  className="flex items-center gap-2 px-3 py-1.5 border border-input rounded-md hover:bg-muted text-sm" data-testid="search-product-btn">
                  <Search className="h-4 w-4" /> Search Product
                </button>
                {showProductSearch && (
                  <div className="absolute right-0 top-full mt-1 w-96 bg-card border border-border rounded-lg shadow-lg z-20 p-3">
                    <input type="text" value={productSearch} onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Search by name, SKU..." autoFocus
                      className="w-full px-3 py-2 border border-input rounded-md text-sm mb-2" data-testid="product-search-input" />
                    <div className="max-h-[28rem] overflow-y-auto">
                      {productResults.length > 0 && (
                        <div className="sticky top-0 bg-muted/80 backdrop-blur px-2 py-1 text-xs text-muted-foreground border-b border-border z-10" data-testid="po-product-results-count">
                          {productResults.length} match{productResults.length === 1 ? '' : 'es'} — scroll to see all, or type more to narrow
                        </div>
                      )}
                      {productResults.map(p => (
                        <button key={p.id} type="button" onClick={() => addProductItem(p)}
                          className="w-full text-left px-3 py-2 hover:bg-muted rounded text-sm" data-testid={`product-option-${p.id}`}>
                          <span className="font-medium">{p.name}</span>
                          <span className="text-muted-foreground ml-2 font-mono text-xs">{p.sku}</span>
                          <span className="text-muted-foreground ml-2">${(p.cost_price || p.unit_price || 0).toFixed(2)}</span>
                        </button>
                      ))}
                      {productResults.length >= 500 && (
                        <div className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border-t border-amber-200 text-center">
                          500+ matches — type more letters to narrow down
                        </div>
                      )}
                      {productSearch.length >= 2 && productResults.length === 0 && (
                        <p className="text-sm text-muted-foreground px-3 py-2">No products found</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button type="button" onClick={addManualItem}
                className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-md hover:bg-primary/90 text-sm" data-testid="add-manual-item-btn">
                <Plus className="h-4 w-4" /> Manual Item
              </button>
            </div>
          </div>
          {/* Barcode scan — each scan adds the matched product as a line item (qty=1) */}
          <div className="px-5 pt-4 pb-2" data-testid="po-barcode-scan-wrapper">
            <label className="block text-xs text-muted-foreground mb-1">Scan barcode to add a line (qty=1)</label>
            <div className="max-w-md">
              <BarcodeScanInput
                onScan={(product) => addProductItem({
                  id: product.product_id,
                  name: product.product_name || product.name,
                  sku: product.sku,
                  variant_id: product.variant_id,
                  cost_price: product.cost_price,
                  unit_price: product.unit_price,
                  description: product.description || '',
                })}
                onMiss={(c) => alert(`Barcode "${c}" not found.`)}
                placeholder="Scan box label or type SKU…"
                autoFocus={false}
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Product</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">SKU</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground w-24">Qty</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground w-28">Unit Cost</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground w-28">Total</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {lineItems.length === 0 ? (
                  <tr><td colSpan="6" className="px-4 py-6 text-center text-sm text-muted-foreground">No items added yet</td></tr>
                ) : (
                  lineItems.map((item, i) => (
                    <tr key={i} data-testid={`po-line-item-${i}`}>
                      <td className="px-4 py-2">
                        <input type="text" value={item.variant_name} onChange={(e) => updateItem(i, 'variant_name', e.target.value)}
                          placeholder="Product name" className="w-full px-2 py-1 border border-input rounded text-sm" />
                      </td>
                      <td className="px-4 py-2">
                        <input type="text" value={item.sku} onChange={(e) => updateItem(i, 'sku', e.target.value)}
                          placeholder="SKU" className="w-full px-2 py-1 border border-input rounded text-sm font-mono" />
                      </td>
                      <td className="px-4 py-2">
                        <input type="number" min="1" value={item.quantity_ordered}
                          onChange={(e) => updateItem(i, 'quantity_ordered', parseInt(e.target.value) || 1)}
                          className="w-full px-2 py-1 border border-input rounded text-sm text-right" />
                      </td>
                      <td className="px-4 py-2">
                        <input type="number" step="0.01" value={item.unit_cost}
                          onChange={(e) => updateItem(i, 'unit_cost', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 border border-input rounded text-sm text-right" />
                      </td>
                      <td className="px-4 py-2 text-right text-sm font-medium tabular-nums">${(item.total || 0).toFixed(2)}</td>
                      <td className="px-4 py-2">
                        <button type="button" onClick={() => removeItem(i)} className="p-1 hover:bg-red-50 rounded text-red-400">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-border bg-muted/30">
            <div className="max-w-xs ml-auto flex justify-between text-lg font-semibold">
              <span>Total</span><span className="tabular-nums">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <button type="button" onClick={() => navigate('/purchase-orders')}
            className="px-6 py-2 border border-input rounded-md hover:bg-muted">Cancel</button>
          <button type="button" onClick={(e) => handleSubmit(e, true)} disabled={saving}
            className="px-6 py-2 border border-input rounded-md hover:bg-muted disabled:opacity-50" data-testid="save-draft-po-btn">
            {saving ? 'Saving...' : 'Save as Draft'}
          </button>
          <button type="submit" disabled={saving}
            className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50" data-testid="submit-po-btn">
            {saving ? 'Creating...' : 'Create Purchase Order'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreatePurchaseOrder;
